import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';

export default function SecureVideoPlayer({ videoUrl, title, thumbnailUrl }) {
  const [player, setPlayer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(100);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [qualities, setQualities] = useState([]);
  const [activeQuality, setActiveQuality] = useState('auto');
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  
  const [userEmail, setUserEmail] = useState('guest@engimitra.in');
  const [watermarkPos, setWatermarkPos] = useState({ top: '20%', left: '20%' });
  
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  // Get User Email for Watermark
  useEffect(() => {
    async function getUser() {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        setUserEmail(user.email);
      }
    }
    getUser();
  }, []);

  // Move watermark periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setWatermarkPos({
        top: `${Math.floor(Math.random() * 60) + 20}%`,
        left: `${Math.floor(Math.random() * 60) + 20}%`,
      });
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Time update loop
  useEffect(() => {
    let interval;
    if (isPlaying && player) {
      interval = setInterval(() => {
        setCurrentTime(player.getCurrentTime());
      }, 500);
    }
    return () => clearInterval(interval);
  }, [isPlaying, player]);

  // Load YouTube API
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    const onYouTubeIframeAPIReady = () => {
      const videoId = extractVideoId(videoUrl);
      new window.YT.Player(iframeRef.current, {
        videoId: videoId,
        playerVars: {
          modestbranding: 1,
          rel: 0,
          controls: 0,
          showinfo: 0,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
          autohide: 1
        },
        events: {
          onReady: (event) => {
            setPlayer(event.target);
            setDuration(event.target.getDuration());
            setQualities(event.target.getAvailableQualityLevels() || []);
          },
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              setHasStarted(true);
              // Refresh qualities when video starts playing
              const available = event.target.getAvailableQualityLevels();
              if (available && available.length > 0) {
                setQualities(available);
              }
            } else {
              setIsPlaying(false);
            }
          }
        }
      });
    };

    if (window.YT && window.YT.Player) {
      onYouTubeIframeAPIReady();
    } else {
      window.onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;
    }

    return () => {
      if (player) player.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl]);

  useEffect(() => {
    setHasStarted(false);
    setIsPlaying(false);
    setCurrentTime(0);
  }, [videoUrl]);

  const extractVideoId = (url) => {
    if (!url) return '';
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : url;
  };

  const handlePlayPause = (e) => {
    if (e) e.stopPropagation();
    if (!player) return;
    if (isPlaying) {
      player.pauseVideo();
    } else {
      player.playVideo();
    }
  };

  const skip = (seconds) => {
    if (!player) return;
    const newTime = player.getCurrentTime() + seconds;
    player.seekTo(Math.max(0, Math.min(newTime, duration)), true);
    setCurrentTime(newTime);
  };

  const handleSeek = (e) => {
    if (!player) return;
    const newTime = (parseFloat(e.target.value) / 100) * duration;
    player.seekTo(newTime, true);
    setCurrentTime(newTime);
  };

  const toggleMute = () => {
    if (!player) return;
    if (isMuted) {
      player.unMute();
      setIsMuted(false);
    } else {
      player.mute();
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (e) => {
    const val = parseInt(e.target.value);
    setVolume(val);
    if (player) {
      player.setVolume(val);
      if (val > 0) {
        player.unMute();
        setIsMuted(false);
      } else {
        player.mute();
        setIsMuted(true);
      }
    }
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const changeQuality = (q) => {
    if (player) {
      player.setPlaybackQuality(q);
      setActiveQuality(q);
      setShowQualityMenu(false);
    }
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  return (
    <div 
      ref={containerRef}
      className="secure-player-container"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      style={{
        position: 'relative',
        width: '100%',
        paddingBottom: '56.25%',
        backgroundColor: '#000',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
        border: '1px solid rgba(255,255,255,0.05)',
        cursor: showControls ? 'default' : 'none'
      }}
    >
      {/* 1. The Video */}
      <div 
        ref={iframeRef}
        style={{
          position: 'absolute',
          top: 0, left: 0, width: '100%', height: '100%',
          pointerEvents: 'none'
        }}
      ></div>

      {/* 2. Custom Thumbnail */}
      {!hasStarted && thumbnailUrl && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 5, backgroundColor: '#000' }}>
          <img src={thumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }} />
        </div>
      )}

      {/* 3. The Glass Shield */}
      <div 
        className="glass-shield"
        onClick={handlePlayPause}
        onContextMenu={(e) => e.preventDefault()}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 10,
          background: 'transparent'
        }}
      ></div>

      {/* 4. Watermark */}
      <div 
        style={{
          position: 'absolute',
          top: watermarkPos.top,
          left: watermarkPos.left,
          zIndex: 15,
          color: 'rgba(255, 255, 255, 0.12)',
          fontSize: '0.75rem',
          fontFamily: 'monospace',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          transition: 'all 2s ease-in-out',
          textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
        }}
      >
        {userEmail} • Protected by ENGImitra
      </div>

      {/* 5. Central Play/Pause Overlay */}
      {(!isPlaying || showControls) && (
        <div 
          onClick={handlePlayPause}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isPlaying ? 'transparent' : 'rgba(0,0,0,0.3)',
            transition: 'opacity 0.4s',
            opacity: showControls ? 1 : 0,
            cursor: 'pointer'
          }}
        >
          {!isPlaying && (
            <div style={{
              width: '80px', height: '80px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #00f0ff, #00c4b4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 30px rgba(0,240,255,0.4)'
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="#002022"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
            </div>
          )}
        </div>
      )}

      {/* 6. Custom Control Bar */}
      <div 
        className="custom-controls"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          bottom: 0, left: 0, right: 0,
          zIndex: 30,
          background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
          padding: '2rem 1.5rem 1rem',
          transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s',
          transform: showControls ? 'translateY(0)' : 'translateY(100%)',
          opacity: showControls ? 1 : 0
        }}
      >
        {/* Progress Bar */}
        <div style={{ position: 'relative', marginBottom: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <input 
            type="range" 
            min="0" max="100" 
            value={duration ? (currentTime / duration) * 100 : 0} 
            onChange={handleSeek}
            style={{
              width: '100%', height: '4px',
              appearance: 'none', 
              background: `linear-gradient(to right, var(--cyan) 0%, var(--cyan) ${duration ? (currentTime / duration) * 100 : 0}%, rgba(255,255,255,0.2) ${duration ? (currentTime / duration) * 100 : 0}%, rgba(255,255,255,0.2) 100%)`,
              borderRadius: '2px', cursor: 'pointer', outline: 'none'
            }}
          />
        </div>

        {/* Buttons Row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.2rem' }}>
            {/* Play/Pause */}
            <button onClick={handlePlayPause} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                {isPlaying ? <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/> : <polygon points="5 3 19 12 5 21 5 3"/>}
              </svg>
            </button>

            {/* Skip Back */}
            <button onClick={() => skip(-10)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 17l-5-5 5-5M18 17l-5-5 5-5"/></svg>
            </button>

            {/* Skip Forward */}
            <button onClick={() => skip(10)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 17l5-5-5-5M6 17l5-5-5-5"/></svg>
            </button>

            {/* Volume */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
              <button onClick={toggleMute} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {isMuted || volume === 0 ? <path d="M11 5L6 9H2v6h4l5 4V5zM23 9l-6 6M17 9l6 6"/> : <path d="M11 5L6 9H2v6h4l5 4V5zM19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>}
                </svg>
              </button>
              <input 
                type="range" min="0" max="100" value={isMuted ? 0 : volume} 
                onChange={handleVolumeChange}
                style={{ width: '60px', accentColor: 'var(--cyan)' }}
              />
            </div>

            {/* Time Display */}
            <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace' }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1.2rem' }}>
            {/* Quality Selector */}
            <div style={{ position: 'relative' }}>
              <button 
                onClick={() => setShowQualityMenu(!showQualityMenu)}
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer' }}
              >
                {activeQuality.toUpperCase()}
              </button>
              {showQualityMenu && (
                <div style={{
                  position: 'absolute', bottom: '100%', right: 0, marginBottom: '10px',
                  background: 'rgba(20,20,20,0.95)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px', overflow: 'hidden', minWidth: '120px', backdropFilter: 'blur(10px)'
                }}>
                  {/* Auto Option */}
                  <div 
                    onClick={() => changeQuality('auto')}
                    style={{ padding: '0.6rem 1rem', fontSize: '0.75rem', cursor: 'pointer', color: activeQuality === 'auto' ? 'var(--cyan)' : '#fff', background: activeQuality === 'auto' ? 'rgba(0,229,200,0.1)' : 'transparent' }}
                  >
                    Auto (Recommended)
                  </div>

                  {/* Filtered & Mapped Qualities */}
                  {(() => {
                    const map = {
                      'hd1080': '1080p Full HD',
                      'hd720': '720p HD',
                      'medium': '360p SD',
                      'tiny': '144p Data Saver'
                    };
                    
                    const allowed = ['hd1080', 'hd720', 'medium', 'tiny'];
                    
                    return qualities
                      .filter(q => allowed.includes(q))
                      .sort((a, b) => allowed.indexOf(a) - allowed.indexOf(b))
                      .map(q => (
                        <div 
                          key={q} 
                          onClick={() => changeQuality(q)}
                          style={{ padding: '0.6rem 1rem', fontSize: '0.75rem', cursor: 'pointer', color: activeQuality === q ? 'var(--cyan)' : '#fff', background: activeQuality === q ? 'rgba(0,229,200,0.1)' : 'transparent' }}
                        >
                          {map[q]}
                        </div>
                      ));
                  })()}
                </div>
              )}
            </div>

            {/* Fullscreen */}
            <button onClick={toggleFullscreen} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
